import { useMemo } from 'react';
import { createBrowserRouter, RouterProvider, useNavigate, useSearchParams, Outlet } from 'react-router-dom';
import { AuthProvider } from './context/AuthProvider';
import { useAuth } from './hooks/useAuth';
import { useApiCheck } from './hooks/useApiCheck';
import { getAuthCheck } from './api/ApiClient';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import AuthenticatedHome from './pages/AuthenticatedHome';
import ProfileClaimsPage from './pages/ProfileClaimsPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import { TokenStore } from './auth/TokenStore';
/* ------------------------------------------------------------------ */
/*  Root layout — single AuthProvider wrapping all routes             */
/* ------------------------------------------------------------------ */
function RootLayout() {
  return (
    <AuthProvider>
      <Layout>
        <Outlet />
      </Layout>
    </AuthProvider>
  );
}

/* ------------------------------------------------------------------ */
/*  Landing page wrapper — reads error state from search params        */
/* ------------------------------------------------------------------ */
function LandingPageWrapper() {
  const { login, error } = useAuth();
  const [searchParams] = useSearchParams();

  const errorParam = searchParams.get('error');
  let authError: { kind: 'NOT_AUTHORIZED' | 'UNKNOWN'; message: string } | null = null;

  if (errorParam === 'not_authorized') {
    authError = { kind: 'NOT_AUTHORIZED', message: 'Access denied. Only @hopper.dev accounts are allowed.' };
  } else if (errorParam === 'callback_failed') {
    authError = { kind: 'UNKNOWN', message: 'Authentication failed. Please try again.' };
  } else if (error?.kind === 'NOT_AUTHORIZED') {
    authError = { kind: 'NOT_AUTHORIZED', message: error.message };
  }

  return (
    <LandingPage
      onLogin={() => login({ returnPath: '/app' })}
      authError={authError}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Authenticated Home wrapper — connects hooks and services           */
/* ------------------------------------------------------------------ */
function AuthenticatedHomeWrapper() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Adapter: ApiClient returns ApiResult<AuthCheckApiResponse>; the hook expects
  // a function that returns Promise<ApiCheckResponseDto> directly, throwing on error.
  const apiClient = {
    getAuthCheck: async (idToken: string) => {
      const result = await getAuthCheck(idToken);
      if (result.ok) {
        return {
          requestId: result.data.requestId,
          subject: result.data.subject,
          email: result.data.email,
        };
      }
      const err = new Error(result.error) as Error & { status: number };
      err.status = result.status;
      throw err;
    },
  };

  const { state: apiCheckState, run: runApiCheck } = useApiCheck({
    apiClient,
    getIdToken: () => TokenStore.getTokens()?.idToken ?? null,
    onAuthExpired: () => {
      logout();
    },
  });

  return (
    <AuthenticatedHome
      user={{
        email: user?.email,
        name: user?.name,
      }}
      onViewProfile={() => navigate('/claims')}
      onLogout={() => logout()}
      apiCheck={{
        isLoading: apiCheckState.isLoading,
        result: apiCheckState.data
          ? { ok: true, subject: apiCheckState.data.subject, email: apiCheckState.data.email }
          : null,
        error: apiCheckState.error
          ? { message: apiCheckState.error.message, kind: apiCheckState.error.kind }
          : null,
        run: runApiCheck,
      }}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  App — router created inside component to defer browser API access  */
/* ------------------------------------------------------------------ */
export default function App() {
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          element: <RootLayout />,
          children: [
            {
              path: '/',
              element: <LandingPageWrapper />,
            },
            {
              path: '/auth/callback',
              element: <AuthCallbackPage />,
            },
            {
              element: <ProtectedRoute loadingFallback={<div />} />,
              children: [
                {
                  path: '/app',
                  element: <AuthenticatedHomeWrapper />,
                },
                {
                  path: '/claims',
                  element: <ProfileClaimsPage />,
                },
                {
                  path: '/profile',
                  element: <ProfileClaimsPage />,
                },
              ],
            },
          ],
        },
      ]),
    [],
  );

  return <RouterProvider router={router} />;
}
